import React, { useEffect, useState } from "react";
import { Container, Table, Spinner, Button, Pagination, Row, Form, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FireStoreOrder } from "../../model/OrderModel";
import { OrderGateway } from "../../gateways/OrderGateway";
import { Utils } from "../../common/Utils";
import { useGlobal } from "../../contexts/GlobalContext";

const Orders: React.FC = () => {
    const [orders, setOrders] = useState<FireStoreOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const ordersPerPage = 12; // Number of orders per page
    const navigate = useNavigate();
    const [statusFilter, setStatusFilter] = useState<string>("");
    const [nameFilter, setNameFilter] = useState<string>("");
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const {settings, user} = useGlobal();

    useEffect(() => {
        const fetchOrders = async () => {

            if (!user) {
                console.log("No user is signed in.");
                setLoading(false);
                return;
            }

            const orderList = await OrderGateway.GetOrders(null);
            orderList.sort((a, b) => new Date(b.order.createdAt).getTime() - new Date(a.order.createdAt).getTime());
            setOrders(orderList);
            setLoading(false);
        };

        fetchOrders();
    }, [user]);

    // Apply filters
    const filteredOrders = orders.filter(fireStoreOrder => {
        const matchesStatus = statusFilter === "" || fireStoreOrder.order.status.toLowerCase() === statusFilter.toLowerCase();
        const matchesName =
            nameFilter === "" ||
            fireStoreOrder.order.firstName.toLowerCase().includes(nameFilter.toLowerCase()) ||
            fireStoreOrder.order.lastName.toLowerCase().includes(nameFilter.toLowerCase());

        const matchesDate =
            (!startDate || new Date(fireStoreOrder.order.createdAt) >= new Date(startDate)) &&
            (!endDate || new Date(fireStoreOrder.order.createdAt) <= new Date(endDate));

        return matchesStatus && matchesName && matchesDate;
    });


    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, nameFilter]);

    const handleCSVDownload = () => {
        try {

            // const headers = ["Order ID", "Date", "Status", "Customer", "Delivery Location", "Total Amount", "Cart Items"];

            // const rows = orders.map(order => {
            //     const cartItems = order.cartItems.map(item =>
            //         `${item.name} (Qty: ${item.quantity}| Price: ${item.priceAfterTax})`
            //     ).join("; ");

            //     return [order.id, order.createdAt, order.status, `${order.firstName} ${order.lastName}`, order.deliveryLocation, `${order.totalAmount}`, cartItems];
            // });

            const headers = ["Order ID", "Date", "Status", "Customer", "Delivery Location", "Delivery Fee", "Total Amount", "Cart Item Name", "Quantity", "Cart Item Amount"];

            const rows = orders.flatMap(fireStoreOrder =>
                fireStoreOrder.order.cartItems.map(item => [
                    fireStoreOrder.order.orderNumber,
                    fireStoreOrder.order.createdAt,
                    fireStoreOrder.order.status,
                    `${fireStoreOrder.order.firstName} ${fireStoreOrder.order.lastName}`,
                    fireStoreOrder.order.deliveryLocation || "Collection",
                    `${fireStoreOrder.order.deliveryFee}`,
                    `${fireStoreOrder.order.totalAmount}`,
                    item.name,
                    item.quantity,
                    `${item.priceAfterTax}`
                ])
            );


            // Format CSV content
            const csvContent = [headers, ...rows]
                .map(row => row.join(","))
                .join("\n");

            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "orders.csv"
            link.click();
        } catch (error) {
            console.error("Error exporting CSV:", error);
        }
    };


    // Pagination logic
    const isDateFilterApplied = startDate !== "" || endDate !== "";
    const indexOfLastOrder = currentPage * ordersPerPage;
    const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
    const currentOrders = isDateFilterApplied ? filteredOrders : filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
    const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

    // Calculate total footer values
    const totalAmountSum = filteredOrders.reduce((acc, fireStoreOrder) => acc + Number(fireStoreOrder.order.totalAmount), 0);
    
    return (
        <Container className="main-container mt-4">
            <div className="d-flex justify-content-between align-items-center">
                <h2>Orders</h2>
                {/* Excel Icon Button */}
                <Button variant="primary" onClick={handleCSVDownload}>
                    Export to CSV
                </Button>
            </div>

            {/* Filters */}
            <Row className="mb-3">
                <Col md={4}>
                    <Form.Group controlId="statusFilter">
                        <h6>Filter by Status:</h6>
                        <Form.Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                            <option value="">All</option>
                            <option value="New">New</option>
                            <option value="In-progress">In-progress</option>
                            <option value="Completed">Completed</option>
                            <option value="Cancelled">Cancelled</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group controlId="nameFilter">
                        <h6>Filter by Customer Name:</h6>
                        <Form.Control
                            type="text"
                            placeholder="Enter first name or surname"
                            value={nameFilter}
                            onChange={e => setNameFilter(e.target.value)}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row className="mb-3">
                <Col md={4}>
                    <Form.Group controlId="startDateFilter">
                        <h6>Start Date:</h6>
                        <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group controlId="endDateFilter">
                        <h6>End Date:</h6>
                        <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                    </Form.Group>
                </Col>
            </Row>

            {loading ? (
                <Spinner animation="border" />
            ) : currentOrders.length === 0 ? (
                <p className="text-center">You have no orders in the selected filters.</p>
            ) : (
                <>
                    <Table striped bordered hover responsive>
                        <colgroup>
                            <col style={{ width: "15%" }} />{/* Order ID column */}
                            <col style={{ width: "20%" }} />{/* Order summary column */}
                            <col style={{ width: "15%" }} />{/* Date column */}
                            <col style={{ width: "10%" }} />{/* Total Amount column */}
                            <col style={{ width: "10%" }} />{/* Status column */}
                            <col style={{ width: "10%" }} />{/* Customer column */}
                            <col style={{ width: "10%" }} />{/* Delivery column */}
                            <col style={{ width: "10%" }} />{/* Action column */}
                        </colgroup>
                        <thead className="table-dark">
                            <tr>
                                <th>Order ID</th>
                                <th>Order Summary</th>
                                <th>Date</th>
                                <th>Total Amount</th>
                                <th>Status</th>
                                <th>Customer</th>
                                <th>Delivery location</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentOrders.map(fireStoreOrder => (
                                <tr key={fireStoreOrder.id}>
                                    <td>{fireStoreOrder.order.orderNumber}</td>
                                    <td>
                                        {fireStoreOrder.order.cartItems.length > 1
                                            ? `${fireStoreOrder.order.cartItems[0].name} x ${fireStoreOrder.order.cartItems[0].quantity}, ${fireStoreOrder.order.cartItems[1].name}  x ${fireStoreOrder.order.cartItems[1].quantity} ${fireStoreOrder.order.cartItems.length > 2 ? "...":""}`
                                            : fireStoreOrder.order.cartItems.length > 0 ? `${fireStoreOrder.order.cartItems[0].name} x ${fireStoreOrder.order.cartItems[0].quantity}`
                                                : "No items"
                                        }
                                    </td>
                                    <td>{new Date(fireStoreOrder.order.createdAt).toLocaleString()}</td>
                                    <td className="text-end align-middle">{Utils.FormatCurrency(Number(fireStoreOrder.order.totalAmount), settings.currency)}</td>
                                    <td>
                                        <span className={`badge ${fireStoreOrder.order.status === "Completed" ? "bg-success" : fireStoreOrder.order.status === "Cancelled" ? "bg-danger" : "bg-warning text-dark"}`}>
                                            {fireStoreOrder.order.status}
                                        </span>
                                    </td>
                                    <td>
                                        {`${fireStoreOrder.order.firstName} ${fireStoreOrder.order.lastName}`}
                                    </td>
                                    <td>
                                        {fireStoreOrder.order.deliveryLocation !== "" ? fireStoreOrder.order.deliveryLocation : "Collection"}
                                    </td>
                                    <td>
                                        <Button variant="primary" onClick={() => navigate(Utils.GetUrl(`/admin/order/${fireStoreOrder.id}`))}>
                                            View Details
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        {isDateFilterApplied && (
                            <tfoot>
                                <tr>
                                    <td colSpan={2}><strong>Total:</strong></td>
                                    <td className="text-end"><strong>{totalAmountSum.toFixed(2)}</strong></td>
                                    <td colSpan={4}></td>
                                </tr>
                            </tfoot>
                        )}

                    </Table>
                    {/* Pagination Controls */}
                    {!isDateFilterApplied && (
                        <Pagination className="mt-4 justify-content-center">
                            <Pagination.Prev
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            />
                            {Array.from({ length: totalPages }, (_, i) => (
                                <Pagination.Item
                                    key={i + 1}
                                    active={i + 1 === currentPage}
                                    onClick={() => setCurrentPage(i + 1)}
                                >
                                    {i + 1}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                            />
                        </Pagination>)}
                </>
            )}
        </Container>
    );
};

export default Orders;
